import { Alert, Button, Grid2 as Grid, Theme, useMediaQuery } from '@mui/material'

import { Revenue } from './revenue'
import { FiscalNotes } from './fiscal-notes'
import { CompanyData } from './company-data'
import { MonthlyDocuments } from './monthly-documents'
import {
  PageHeaderContainer,
  AccountantTransferStatusAlert,
  AccountantTransferStatusDialog,
  CompanyOpeningStatusDialog,
  CompanyOpeningStatusAlert,
  toCurrentStep,
  ChangingAccountantWelcomeMessage,
  ChangingAccountantDocumentsAlert,
  InsuranceAnnouncementDialog,
  RouterLink,
} from '@/components'
import { DashboardHeader } from './header'
import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  useGetCompanyAccountantTransferProcess,
  useGetCompanyOpeningProcess,
} from '@/api/accounting-dealer'
import { useCompany } from '@/context/companies'
import { useReadUserProfile } from '@/api/user-manager'
import { hasSeenAnnouncement, markAnnouncementSeen } from '@/helpers'
import { routes } from '@/router'
import { Taxes } from './taxes'

export function DashboardPage() {
  const { company } = useCompany()
  const { data: user } = useReadUserProfile()
  const navigate = useNavigate()

  const userHasVerifiedPhone = !!user?.alias?.find(
    (alias) => alias.type === 'phone' && alias.verified,
  )

  const isMobile = useMediaQuery<Theme>((theme) => theme.breakpoints.down('md'))

  const isChangingAccountant = useMemo(() => {
    return company.status === 'changing_accountant'
  }, [company])

  const [
    isChangingAccountantWelcomeMessageOpen,
    setIsChangingAccountantWelcomeMessageOpen,
  ] = useState(true)

  const toggleChangingAccountantWelcomeMessage = () => {
    setIsChangingAccountantWelcomeMessageOpen((prev) => !prev)
  }

  // const { data: waitingRecords } = useReadFinancialRecordRequests({
  //   status: 'waiting',
  //   tax_id: company.tax_id,
  // })

  // const hasPendingServices = useMemo(
  //   () => waitingRecords && waitingRecords.data.length > 0,
  //   [waitingRecords],
  // )

  const { data: companyOpeningProcess, isFetched: openingProcessFetched } =
    useGetCompanyOpeningProcess(company.key)

  const { data: accountantTransferProcess } =
    useGetCompanyAccountantTransferProcess(company.key, {
      enabled: isChangingAccountant,
    })

  const currentStep = useMemo(() => {
    if (!companyOpeningProcess?.current_phase) return undefined
    return toCurrentStep(companyOpeningProcess.current_phase)
  }, [companyOpeningProcess])

  const accountantTransferStep = useMemo(() => {
    if (!accountantTransferProcess?.current_phase) return undefined
    if (accountantTransferProcess.card_status === 'completed') return undefined
    return toCurrentStep(accountantTransferProcess.current_phase)
  }, [accountantTransferProcess])

  const showChangingAccountantOnboarding = Boolean(
    isChangingAccountant &&
      accountantTransferProcess &&
      accountantTransferProcess.card_status !== 'completed',
  )

  const hasOngoingProcess =
    company.status === 'opening_process' ||
    isChangingAccountant ||
    Boolean(
      companyOpeningProcess?.process_key &&
        companyOpeningProcess.card_status !== 'completed',
    ) ||
    Boolean(
      accountantTransferProcess?.process_key &&
        accountantTransferProcess.card_status !== 'completed',
    )

  const [
    isInsuranceAnnouncementClosed,
    setIsInsuranceAnnouncementClosed,
  ] = useState(false)

  const showInsuranceAnnouncement =
    Boolean(user?.key) &&
    openingProcessFetched &&
    !hasOngoingProcess &&
    !isInsuranceAnnouncementClosed &&
    !hasSeenAnnouncement('insurance-launch', user!.key)

  const handleCloseInsuranceAnnouncement = () => {
    if (user?.key) {
      markAnnouncementSeen('insurance-launch', user.key)
    }
    setIsInsuranceAnnouncementClosed(true)
  }

  const handleConfirmInsuranceAnnouncement = () => {
    handleCloseInsuranceAnnouncement()
    navigate(routes.insurance)
  }

  const [
    isCompanyOpeningStatusDialogOpen,
    setIsCompanyOpeningStatusDialogOpen,
  ] = useState(false)

  const toggleCompanyOpeningStatusDialog = () => {
    setIsCompanyOpeningStatusDialogOpen((prev) => !prev)
  }

  const [
    isAccountantTransferStatusDialogOpen,
    setIsAccountantTransferStatusDialogOpen,
  ] = useState(false)

  const toggleAccountantTransferStatusDialog = () => {
    setIsAccountantTransferStatusDialogOpen((prev) => !prev)
  }

  return (
    <>
      <PageHeaderContainer>
        <DashboardHeader />
      </PageHeaderContainer>

      <Grid container spacing={2}>
        {/* {hasPendingServices && (
        <Grid size={12}>
          <Alert
            icon={false}
            severity="warning"
            action={
              <Button
                size="small"
                variant="text"
                color="warning"
                component={Link}
                to={routes.additionalServices}
              >
                Revisar
              </Button>
            }
          >
            Revisar serviços adicionais pendentes
          </Alert>
        </Grid>
        )} */}

        {user && userHasVerifiedPhone === false && (
          <Grid size={12}>
          <Alert
            icon={false}
            severity="warning"
            action={
              <Button
                size="small"
                variant="text"
                color="warning"
                component={RouterLink}
                to={routes.account}
              >
                Verificar telefone
              </Button>
            }
          >
            Verifique seu telefone para continuar com o cadastro da empresa
          </Alert>
        </Grid>
        )}

        {accountantTransferStep && (
          <Grid size={12}>
            <AccountantTransferStatusAlert
              currentStep={accountantTransferStep}
              cardStatus={accountantTransferProcess?.card_status ?? 'in_progress'}
              onOpenDialog={toggleAccountantTransferStatusDialog}
            />
          </Grid>
        )}

        {showChangingAccountantOnboarding && (
          <Grid size={12}>
            <ChangingAccountantDocumentsAlert />
          </Grid>
        )}

        {currentStep && (
          <Grid size={12}>
            <CompanyOpeningStatusAlert
              currentStep={currentStep}
              cardStatus={companyOpeningProcess?.card_status ?? 'in_progress'}
              onOpenDialog={toggleCompanyOpeningStatusDialog}
            />
          </Grid>
        )}

        <Grid size={12}>
          <Revenue />
        </Grid>

        <Grid
          size={{
            xs: 6,
            md: 12,
          }}
        >
          <FiscalNotes />
        </Grid>

        {isMobile && (
          <Grid size={6}>
            <MonthlyDocuments />
          </Grid>
        )}

        <Grid
          size={{
            xs: 12,
            md: 6,
          }}
        >
          <Taxes />
        </Grid>

        <Grid size={12}>
          <CompanyData />
        </Grid>
      </Grid>

      {showChangingAccountantOnboarding && (
        <ChangingAccountantWelcomeMessage
          open={isChangingAccountantWelcomeMessageOpen}
          onClose={toggleChangingAccountantWelcomeMessage}
          engagementDate={company.engagement_date}
        />
      )}

      {accountantTransferStep && accountantTransferProcess && (
        <AccountantTransferStatusDialog
          open={isAccountantTransferStatusDialogOpen}
          onClose={toggleAccountantTransferStatusDialog}
          currentStep={accountantTransferStep}
          phases={accountantTransferProcess.phases}
        />
      )}

      {currentStep && companyOpeningProcess && (
        <CompanyOpeningStatusDialog
          open={isCompanyOpeningStatusDialogOpen}
          onClose={toggleCompanyOpeningStatusDialog}
          currentStep={currentStep}
          phases={companyOpeningProcess.phases}
        />
      )}

      {showInsuranceAnnouncement && (
        <InsuranceAnnouncementDialog
          open
          onClose={handleCloseInsuranceAnnouncement}
          onConfirm={handleConfirmInsuranceAnnouncement}
        />
      )}
    </>
  )
}
